import { Button, Card, Drawer, Flex, Hr, InfoCard, Input, Link, P, Section, Tag, ToggleButton } from '@dnb/eufemia';
import { filter as FilterIcon } from '@dnb/eufemia/icons';
import styled from '@emotion/styled';
import { type ApiFromCommonRepoDto, COMMON_REPO_ENVIRONMENTS } from '@portals/shared/portal/ApiFromCommonRepoDto';
import { TrackRoute } from '@portals/shared-frontend/components';
import { type JSX, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import useSWR from 'swr';

import Page from '@/components/Page';

export default function ApiFromCommonRepo(): JSX.Element {
  const [classificationFilter, setClassificationFilter] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const { data: apis, isValidating: apisLoading } = useSWR<ApiFromCommonRepoDto[]>('/common-repo/apis');
  const [envFilter, setEnvFilter] = useState<string[]>([]);

  const hasLoaded = !apisLoading && !!apis;

  const filteredApis = useMemo(() => {
    if (!apis) return [];

    const lowerCasedQuery = query.toLowerCase();

    const basis = apis
      .filter((api) => (classificationFilter.length > 0 ? classificationFilter.includes(api.classification) : true))
      .filter((api) =>
        envFilter.length > 0 ? envFilter.some((env) => api.versions.map((v) => v.environment).includes(env)) : true,
      )
      .filter((api) => !lowerCasedQuery || api.title.toLowerCase().includes(lowerCasedQuery));

    return basis;
  }, [apis, envFilter, classificationFilter, query]);

  const classifications = useMemo(() => {
    if (apis) {
      return [...new Set(apis.map((api) => api.classification))];
    }
    return [];
  }, [apis]);

  return (
    <div className="dark-mode">
      <Page innerSpace={false} skeleton={!hasLoaded} styleType="white" tabTitle="API contracts" title="API contracts">
        <TrackRoute name="API Contracts" path="/api-contracts" type="contract" />
        <Section backgroundColor="white" top="large">
          <FlexFormRow bottom="small">
            <Drawer
              right="x-small"
              title="Filters"
              triggerAttributes={{
                text: 'Filter',
                variant: 'secondary',
                icon: FilterIcon,
              }}
            >
              <Flex.Vertical>
                <Flex.Horizontal align="baseline" top="medium">
                  <ToggleButton.Group
                    label={<P weight="medium">Classifications</P>}
                    multiselect
                    onChange={({ values }) => {
                      setClassificationFilter([...values].map(String));
                    }}
                    values={classificationFilter}
                    variant="checkbox"
                  >
                    {classifications.map((classification) => (
                      <CapitalizedToggleButton key={classification} text={classification} value={classification} />
                    ))}
                  </ToggleButton.Group>
                </Flex.Horizontal>
                <Flex.Horizontal align="baseline" top="medium">
                  <ToggleButton.Group
                    label={<P weight="medium">Environments</P>}
                    multiselect
                    onChange={({ values }) => {
                      setEnvFilter([...values].map(String));
                    }}
                    values={envFilter}
                    variant="checkbox"
                  >
                    {COMMON_REPO_ENVIRONMENTS.map((env) => (
                      <CapitalizedToggleButton key={env} text={env} value={env} />
                    ))}
                  </ToggleButton.Group>
                </Flex.Horizontal>
              </Flex.Vertical>
            </Drawer>
            <Input
              icon="loupe"
              onChange={({ value }) => setQuery(value)}
              placeholder="Search"
              showClearButton
              top="small"
              value={query}
            />
          </FlexFormRow>
          {classificationFilter.length > 0 && (
            <Flex.Horizontal align="baseline" bottom="small">
              <Tag.Group label="Classifications">
                <P>Classifications: </P>
                {classificationFilter.map((classification) => (
                  <CapitalizedTag
                    key={classification}
                    onClick={() => {
                      const _classificationFilter = classificationFilter.filter(
                        (candidate) => candidate !== classification,
                      );
                      setClassificationFilter(_classificationFilter);
                    }}
                    variant="removable"
                  >
                    {classification}
                  </CapitalizedTag>
                ))}
              </Tag.Group>
            </Flex.Horizontal>
          )}
          {envFilter.length > 0 && (
            <Flex.Horizontal align="baseline" bottom="small">
              <Tag.Group label="Environments">
                <P>Environments: </P>
                {envFilter.map((env) => (
                  <CapitalizedTag
                    key={env}
                    onClick={() => {
                      const _envFilter = envFilter.filter((candidate) => candidate !== env);
                      setEnvFilter(_envFilter);
                    }}
                    variant="removable"
                  >
                    {env}
                  </CapitalizedTag>
                ))}
              </Tag.Group>
            </Flex.Horizontal>
          )}

          {filteredApis.length > 0 ? (
            filteredApis.map((api) => (
              <Card bottom="medium" key={api.slug}>
                <Link element={RouterLink} to={`${api.slug}/@default/@latest`}>
                  {api.title}
                </Link>
                <SubText top="small">{api.classification}</SubText>
                {api.description && (
                  <P bottom="small" top="small">
                    {api.description.length > 300 ? `${api.description.slice(0, 300)} ...` : api.description}
                  </P>
                )}
                <Divider />
                <VersionBadges api={api} />
              </Card>
            ))
          ) : (
            <InfoCard text="No APIs match the selected filters." />
          )}
        </Section>
      </Page>
    </div>
  );
}

function VersionBadges({ api }: { api: ApiFromCommonRepoDto }): JSX.Element {
  const latestVersion = useMemo(() => {
    return COMMON_REPO_ENVIRONMENTS.map((env) => {
      const versionsPerEnv = api.versions.filter((version) => version.environment === env);
      return versionsPerEnv.some((v) => v.isLatest) ? versionsPerEnv.find((v) => v.isLatest) : versionsPerEnv[0];
    }).filter(Boolean);
  }, [api]);
  return (
    <>
      {latestVersion.map((version) => (
        <CapitalizedButton
          element={RouterLink}
          icon="chevron_right"
          key={`${version?.environment}`}
          right="small"
          size="medium"
          text={version?.environment}
          to={`${api.slug}/${version?.environment}/${version?.version}`}
          variant="secondary"
        />
      ))}
    </>
  );
}

const Divider = styled(Hr)`
  color: var(--color-black-8);
  padding-bottom: var(--spacing-small);
`;

const SubText = styled(P)`
  color: var(--color-black-55);
  text-transform: capitalize;
`;

const CapitalizedToggleButton = styled(ToggleButton)`
  .dnb-button {
    text-transform: capitalize;
  }
`;

const CapitalizedButton = styled(Button)`
  text-transform: capitalize;
`;

const CapitalizedTag = styled(Tag)`
  text-transform: capitalize;
`;

const FlexFormRow = styled(Flex.Horizontal)`
  justify-content: space-between;
`;
